import { useState, useEffect } from "react";
import { Alert, Button, Card, Form, Input, Typography } from "antd";
import { LockOutlined } from '@ant-design/icons';
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import supabase from "../../config/supabaseConfig";
import { establishSessionFromUrl } from "../../utils/authHelpers";

const { Title, Text } = Typography;

const ResetPassword = () => {
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const [sessionReady, setSessionReady] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const handleAuthCallback = async () => {
            try {
                const { session, error: sessionError } = await establishSessionFromUrl()

                if (sessionError) {
                    console.error('Session error:', sessionError)
                    setError("Le lien de réinitialisation est invalide ou a expiré. Veuillez demander un nouveau lien.")
                    return
                }

                if (session) {
                    setSessionReady(true)
                } else {
                    setError("Le lien de réinitialisation est invalide ou a expiré. Veuillez demander un nouveau lien.")
                }
            } catch (err) {
                console.error('Auth callback error:', err)
                setError("Une erreur est survenue lors de la vérification du lien.")
            }
        };

        handleAuthCallback();
    }, []);

    const handleSubmit = async () => {
        if (!sessionReady) {
            setError("Session non valide. Veuillez utiliser le lien de réinitialisation de votre email.");
            return;
        }

        if (!password || !confirmPassword) {
            setError("Tous les champs sont obligatoires");
            return;
        }
        if (password !== confirmPassword) {
            setError("Les mots de passe ne correspondent pas");
            return;
        }
        if (password.length < 6) {
            setError("Le mot de passe doit contenir au moins 6 caractères");
            return;
        }

        setError("");
        setSuccess(false);
        setLoading(true);

        try {
            const { error } = await supabase.auth.updateUser({
                password: password
            });

            if (error) throw error;

            setSuccess(true);
            setPassword('');
            setConfirmPassword('');
            setTimeout(() => {
                navigate("/auth/login");
            }, 3000);
        } catch (err) {
            console.error('Password update error:', err);
            setError(err instanceof Error ? err.message : "Une erreur est survenue lors de la réinitialisation du mot de passe");
        } finally {
            setLoading(false);
        }
    };

    if (!sessionReady && !error) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-100 p-4">
                <Card className="w-full max-w-md text-center">
                    <div className="p-4">
                        <Title level={3}>Vérification du lien...</Title>
                        <Text>Veuillez patienter pendant que nous vérifions votre lien de réinitialisation.</Text>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-r from-blue-50 to-indigo-100 p-4">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="w-full max-w-md"
            >
                <Card
                    className="shadow-xl rounded-2xl overflow-hidden"
                    bordered={false}
                    cover={
                        <div className="h-24 bg-gradient-to-r from-blue-600 to-violet-600 flex items-center justify-center">
                            <LockOutlined className="text-white text-4xl" />
                        </div>
                    }
                >
                    <div className="px-2 py-4">
                        <Title level={2} className="text-center mb-6 primary-gradient-text">
                            Réinitialiser le mot de passe
                        </Title>

                        {success ? (
                            <motion.div
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                className="text-center"
                            >
                                <Alert
                                    message="Réinitialisation réussie!"
                                    description="Votre mot de passe a été réinitialisé avec succès. Vous allez être redirigé vers la page de connexion."
                                    type="success"
                                    showIcon
                                    className="mb-4"
                                />
                                <Text className="block text-gray-600 mt-4">
                                    Redirection en cours...
                                </Text>
                            </motion.div>
                        ) : (
                            <Form
                                layout="vertical"
                                name="reset_password"
                                className="login-form"
                                onFinish={handleSubmit}
                                requiredMark="optional"
                            >
                                <Form.Item
                                    label={<label className="text-gray-700 font-medium">Nouveau mot de passe</label>}
                                    name="password"
                                    rules={[
                                        { required: true, message: 'Veuillez saisir votre mot de passe!' },
                                        { min: 6, message: 'Le mot de passe doit contenir au moins 6 caractères!' }
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined className="site-form-item-icon text-gray-400" />}
                                        value={password}
                                        placeholder="Entrez votre nouveau mot de passe"
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="rounded-lg py-2"
                                    />
                                </Form.Item>
                                <Form.Item
                                    label={<label className="text-gray-700 font-medium">Confirmer le mot de passe</label>}
                                    name="confirmPassword"
                                    dependencies={['password']}
                                    rules={[
                                        { required: true, message: 'Veuillez confirmer votre mot de passe!' },
                                        ({ getFieldValue }) => ({
                                            validator(_, value) {
                                                if (!value || getFieldValue('password') === value) {
                                                    return Promise.resolve();
                                                }
                                                return Promise.reject(new Error('Les deux mots de passe ne correspondent pas!'));
                                            },
                                        }),
                                    ]}
                                >
                                    <Input.Password
                                        prefix={<LockOutlined className="site-form-item-icon text-gray-400" />}
                                        value={confirmPassword}
                                        placeholder="Confirmez votre mot de passe"
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="rounded-lg py-2"
                                    />
                                </Form.Item>

                                {error && (
                                    <Alert
                                        message="Erreur"
                                        description={error}
                                        type="error"
                                        showIcon
                                        className="mb-4"
                                    />
                                )}

                                <Form.Item>
                                    <Button
                                        type="primary"
                                        htmlType="submit"
                                        size="large"
                                        loading={loading}
                                        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg h-12 font-medium"
                                    >
                                        Réinitialiser le mot de passe
                                    </Button>
                                </Form.Item>
                            </Form>
                        )}
                    </div>
                </Card>
            </motion.div>
        </div>
    );
};

export default ResetPassword;
